import { Console, Effect, Option, pipe } from "effect";
import { getCampaignMetaArray } from "./get-campaign-meta-array";
import { TokenSet } from "@silo/utils";
import { KvClient } from "../kv-client";
import { MerklCampaign } from "./types";

export type GetCampaignArray = ReturnType<typeof getCampaignArray>;
export type GetCampaignArraySuccess = Effect.Effect.Success<GetCampaignArray>;
export type GetCampaignArrayError = Effect.Effect.Error<GetCampaignArray>;
export type GetCampaignArrayContext = Effect.Effect.Context<GetCampaignArray>;

interface GetCampaignArrayParams {
  activeOnly: boolean;
}

export function getCampaignArray({ activeOnly }: GetCampaignArrayParams) {
  return Effect.gen(function* () {
    yield* Effect.logInfo("Begin 🎬: get merkl campaign array");

    const { campaignMetaArray, source } = yield* Effect.firstSuccessOf([
      pipe(
        KvClient.getMerklCampaignMeta({ activeOnly }),
        Effect.andThen(
          (campaignMetaArray) =>
            ({
              campaignMetaArray,
              source: "kv",
            } as const)
        )
      ),
      pipe(
        getCampaignMetaArray({ activeOnly }),
        Effect.andThen(
          (campaignMetaArray) =>
            ({
              campaignMetaArray,
              source: "merkl api",
            } as const)
        )
      ),
    ]);

    const rewardTokensSet = new TokenSet();

    for (const { chainKey, pricingTokenAddress } of campaignMetaArray) {
      rewardTokensSet.add({ chainKey, tokenAddress: pricingTokenAddress });
    }

    const tokenPriceMap = yield* KvClient.getTokenPriceMap([
      ...rewardTokensSet.values(),
    ]);

    const campaigns: MerklCampaign[] = campaignMetaArray.map((campaignMeta) => {
      const { chainKey, pricingTokenAddress } = campaignMeta;

      return {
        ...campaignMeta,
        rewardTokenPriceUsd: pipe(
          tokenPriceMap.get({ chainKey, tokenAddress: pricingTokenAddress }),
          Option.getOrNull
        ),
      };
    });

    yield* Effect.logInfo(
      `Complete ✅: get merkl campaign array from ${source}`
    );

    return campaigns;
  }).pipe(
    Effect.tapError((error) => Console.error(error)),
    Effect.catchAll((): Effect.Effect<MerklCampaign[]> => Effect.succeed([]))
  );
}
